import React, { useCallback, useEffect, useMemo } from "react";
import { useForm, useFormContext } from "react-hook-form";

import Stack from "@mui/material/Stack";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ModalForm from "components/modal-form";
import SelectFormField from "components/select-form-field";
import { PHONE_TYPES } from "constants/patients";
import PhoneField from "features/patients/patient-form/fields/phone-field.component";
import { useUpdatePatientMutation } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { formatPhoneNumber } from "utils/common";

import { AppointmentFormValues } from "../appointment-form.component";

interface UpdatePatientPhoneModalProps {
  patient: Patient;
  isOpen: boolean;
  onCancel: () => void;
}

const UpdatePatientPhoneModal: React.FC<UpdatePatientPhoneModalProps> = ({
  patient,
  isOpen,
  onCancel
}) => {
  const dispatch = useAppDispatch();

  const [updatePatient, { isLoading: isSubmitting }] =
    useUpdatePatientMutation();

  const { setValue: setParentFormValue } =
    useFormContext<AppointmentFormValues>();

  const defaultValues = useMemo(
    () => ({
      phone: patient.phone ? formatPhoneNumber(patient.phone) || "" : "",
      phone_type: patient.phone_type ?? ""
    }),
    [patient.phone, patient.phone_type]
  );

  const { control, reset, handleSubmit } = useForm<{
    phone: string;
    phone_type: string;
  }>({
    defaultValues,
    mode: "onBlur"
  });

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = useCallback(() => {
    return handleSubmit(({ phone_type, phone }) => {
      return updatePatient({
        id: patient.id,
        phone: phone.replaceAll("-", ""),
        phone_type
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
        } else {
          setParentFormValue(
            "patient.phone",
            response.data.phone ? formatPhoneNumber(response.data.phone) : ""
          );
          setParentFormValue("patient.phone_type", response.data.phone_type);

          dispatch(setCustomMessage(`Patient phone was updated`));
          onCancel();
        }
        dispatch(setSnackbarOpen(true));
      });
    })();
  }, [
    patient.id,
    updatePatient,
    dispatch,
    handleSubmit,
    onCancel,
    setParentFormValue
  ]);

  return (
    <ModalForm
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={onSubmit}
      title={`Update ${patient.fullName}'s phone number`}
      hideBackdrop={false}
      formName="product"
      control={control}
      maxWidth="sm"
    >
      <Stack gap={2}>
        <SelectFormField
          name="phone_type"
          label="Type of Phone number"
          control={control}
          options={PHONE_TYPES.getOptions()}
          required
        />

        <PhoneField name="phone" control={control} required />
      </Stack>
    </ModalForm>
  );
};

export default UpdatePatientPhoneModal;
